import { ReactElement, useContext, useState } from 'react';
import { useRouter } from 'next/router';

import { useModal } from '@appsumo/dorado-react';

import { useEvent } from '~/lib/events';
import { CartContext } from '~/contexts/cart';
import { useCart } from '~/lib/cart';

import UpsellModal from '~/components/cart/cart/UpsellModal/UpsellModal';
import MobileButton from './MobileButton';
import DesktopButton from './DesktopButton';

export default function Summary({
  isMobile,
}: {
  isMobile?: boolean;
}): ReactElement {
  const { cart } = useContext(CartContext);
  const { addUpsellToCart } = useCart(undefined, true);
  const router = useRouter(); // Used useRouter instead of link, cleaner to disable click

  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [isLicensePlanLoading, setIsLicensePlanLoading] = useState(false);
  const [isQuantityLoading, setIsQuantityLoading] = useState(false);
  const [isMoveToCartLoading, setIsMoveToCartLoading] = useState(false);
  const [isSaveDealLoading, setIsSaveDealLoading] = useState(false);
  const [isLineItemRemoveLoading, setIsLineItemRemoveLoading] = useState(false);

  // Show loading states when api requests are in progress
  // Kept these as separate states to prevent race conditions
  // Didn't use 1 loading event for all api calls to allow granular control in other components
  useEvent('coupon:loading', () => setIsCouponLoading(true));
  useEvent('coupon:reset', () => setIsCouponLoading(false));
  useEvent('cart:setLicensePlan:loading', (isLoading) =>
    setIsLicensePlanLoading(isLoading),
  );
  useEvent('cart:lineItemUpdate:loading', (isLoading) =>
    setIsQuantityLoading(isLoading),
  );
  useEvent('cart:lineItemRemove:loading', (isLoading) =>
    setIsLineItemRemoveLoading(isLoading),
  );
  useEvent('cart:saveDeal:loading', (isLoading) =>
    setIsSaveDealLoading(isLoading),
  );
  useEvent('cart:moveToCart:loading', (isLoading) =>
    setIsMoveToCartLoading(isLoading),
  );

  const isCartEmpty = !cart || cart.cart_items.length === 0;
  const isLoading =
    isLicensePlanLoading ||
    isCouponLoading ||
    isQuantityLoading ||
    isLineItemRemoveLoading ||
    isMoveToCartLoading ||
    isSaveDealLoading;

  const [showModal, hideModal] = useModal(
    () => (
      <UpsellModal
        updateCart={async () => {
          await addUpsellToCart(cart);
        }}
        hideModal={hideModal}
        upsellCodeQuantity={cart?.upsell_code_quantity}
        upsellDeal={cart?.upsell_deal}
        upsellPrice={cart?.upsell_price}
      />
    ),
    [cart],
  );

  const onClick = () => {
    cart?.upsell_deal ? showModal() : router.push('/checkout');
  };

  if (isMobile) {
    return (
      <MobileButton
        isLoading={isLoading}
        isDisabled={isLoading || isCartEmpty}
        onClick={onClick}
      />
    );
  }

  return (
    <DesktopButton
      isLoading={isLoading}
      isDisabled={isLoading || isCartEmpty}
      onClick={onClick}
    />
  );
}

export { Summary as CheckoutButton };
